import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdOutlineArrowBack } from 'react-icons/md';
import { Helmet } from 'react-helmet';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import Button from 'components/Button2';
import ButtonLink from 'components/ButtonLink';
import ScoreWithScale from 'components/ScoreWithScale2';

import './index.css';

// 60 minutes for 40 questions.
const timePerQuestion = 60 * 60 / 40;
const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];

type BCAReportProps = {
    // Bulk print mounts this component directly instead of routing to it, so
    // the test id has to be able to arrive as a prop as well as a URL param.
    testIdOverride?: string;
    // Rendered inside the bulk-print container: drop the page chrome (document
    // title, action buttons) so the sheet carries report content only.
    embedded?: boolean;
    // Fires once this report's data has settled, so a bulk print can wait for
    // every report to arrive before opening the print dialog.
    onReady?: () => void;
};

function BCAReport({ testIdOverride, embedded = false, onReady }: BCAReportProps = {}) {
    const [testData, setTestData] = useState(null);

    const { user } = useAuthContext();
    const navigate = useNavigate();
    const { testId: routeTestId } = useParams();
    const testId = testIdOverride ?? routeTestId;
    const printRef = useRef(null);

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        // Marks the print iframe's <body> so the question table is revealed
        // for this single-report print only. Bulk print never sets it.
        bodyClass: 'printing-single-report',
    });

    // `onReady` is read through a ref so a parent passing a fresh closure each
    // render cannot retrigger the fetch -- the effect stays keyed on the id.
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    useEffect(() => {
        if (!testId) return;
        let cancelled = false;
        axios.get(`tests/digital/bca/tutor/result/${testId}/`)
            .then(res => {
                if (cancelled) return;
                setTestData(res.data);
                onReadyRef.current?.();
            })
            .catch(error => {
                console.log(error);
                // Report ready even on failure: one unreachable report must not
                // leave a bulk print waiting for a dialog that never opens.
                if (!cancelled) onReadyRef.current?.();
            });

        return () => {
            cancelled = true;
        }
    }, [testId]);

    if (!testData) return <></>;

    const showCorrectRate = user.customizations.find(customization => customization === 'Show Correct Rate');
    const answers = testData.testModules.flatMap(testModule => testModule.answers);
    const totalTime = answers.reduce((total, answer) => total + answer.timeSpent, 0);

    const timeClass = (timeSpent: number) => (
        timeSpent > timePerQuestion * 1.5
            ? 'bcaTestReport__timeSpentBad'
            : timeSpent > timePerQuestion
            ? 'bcaTestReport__timeSpentWarning'
            : 'bcaTestReport__timeSpentGood'
    );

    const yourAnswer = (answer) => (
        answer.isBlank
            ? <p className='bcaTestReport__questionOmitted'>Omitted</p>
            : answer.isCorrect
            ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
            : <p className='bcaTestReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
    );

    return (
        <div className='bcaTestReport' ref={printRef}>
            {!embedded &&
                <Helmet>
                    <title>
                        Test Report - Test Center Admin
                    </title>
                </Helmet>
            }
            {!embedded && (
                <div className='bcaTestReport__bar'>
                    <button className='bcaTestReport__back' onClick={() => navigate(-1)}>
                        <MdOutlineArrowBack className='bcaTestReport__backIcon' />
                        Back
                    </button>
                </div>
            )}
            <div className='bcaTestReport__headerGroup'>
                <div className='bcaTestReport__headerSection'>
                    {testData.logo &&
                        <img
                            className='bcaTestReport__headerLogo'
                            alt='logo'
                            src={testData.logo}
                        />
                    }
                </div>
                <div className='bcaTestReport__headerSection'>
                    <div className='bcaTestReport__headerInfo'>
                        <p>
                            Name: {testData.studentName}
                        </p>
                        <p>
                            Email: {testData.email}
                        </p>
                        <p>
                            Test name: {testData.name}
                        </p>
                        <p>
                            Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
            </div>
            <div className='bcaTestReport__headerGroup'>
                <h1 className='bcaTestReport__headerTitle'>
                    Test Result Overview
                </h1>
                {!embedded &&
                    <div className='bcaTestReport__buttonGroup'>
                        <ButtonLink className='bcaTestReport__button--blue' to={`/review/digital-bca/${testId}`}>
                            REVIEW QUESTIONS
                        </ButtonLink>
                        <Button className='bcaTestReport__button' secondary onClick={handlePrint}>
                            PRINT REPORT
                        </Button>
                        <ButtonLink className='bcaTestReport__button' to={`/students/${testData.studentId}/digital-bca/performanceHistory`}>
                            PERFORMANCE HISTORY
                        </ButtonLink>
                    </div>
                }
            </div>
            <div className='bcaTestReport__card'>
                <div className='bcaTestReport__cardRow'>
                    <div className='bcaTestReport__scoresGroup'>
                        <h2 className='bcaTestReport__scoreTitle'>TOTAL SCORE</h2>
                        {/* +2 per right answer, -0.5 per wrong one: keep the half point and the sign. */}
                        <ScoreWithScale
                            className='bcaTestReport__scoreWithScale'
                            score={Number(testData.score)}
                            scoreMin={Number(testData.minScore)}
                            scoreMax={Number(testData.maxScore)}
                            fontSize={70}
                        />
                    </div>
                    <div className='bcaTestReport__overview'>
                        <h2 className='bcaTestReport__cardSubTitle'>
                            Raw Scores
                        </h2>
                        <div className='bcaTestReport__overviewRow'>
                            <div className='bcaTestReport__overviewItem'>
                                <table className='bcaTestReport__overviewTable'>
                                    <tbody>
                                        <tr>
                                            <th>Correct</th>
                                            <th>Incorrect</th>
                                            <th>Omitted</th>
                                            <th>Time</th>
                                        </tr>
                                        <tr>
                                            <td>{testData.correct}/{answers.length}</td>
                                            <td>{testData.wrong}</td>
                                            <td>{testData.blank}</td>
                                            <td>{formatTime(totalTime)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <p className='bcaTestReport__scoringNote'>
                            Each correct answer earns 2 points, each incorrect answer loses 0.5 point, and an omitted question earns 0 points.
                        </p>
                    </div>
                </div>
            </div>
            <div className='bcaTestReport__questions'>
                <h2 className='bcaTestReport__questionsHeader'>
                    Questions Overview
                </h2>
                <div className='bcaTestReport__questionsOverview'>
                    <div className='bcaTestReport__questionsSummary'>
                        <div className='bcaTestReport__questionsSummaryItem'>
                            <p className='bcaTestReport__questionSummaryNumber'>{answers.length}</p>
                            <p className='bcaTestReport__questionSummaryText'>Total Questions</p>
                        </div>
                        <div className='bcaTestReport__questionsSummaryItem'>
                            <p className='bcaTestReport__questionSummaryNumber'>{testData.correct}</p>
                            <p className='bcaTestReport__questionSummaryText'>Correct Answers</p>
                        </div>
                        <div className='bcaTestReport__questionsSummaryItem'>
                            <p className='bcaTestReport__questionSummaryNumber'>{testData.wrong}</p>
                            <p className='bcaTestReport__questionSummaryText'>Incorrect Answers</p>
                        </div>
                        <div className='bcaTestReport__questionsSummaryItem'>
                            <p className='bcaTestReport__questionSummaryNumber'>{testData.blank}</p>
                            <p className='bcaTestReport__questionSummaryText'>Omitted</p>
                        </div>
                    </div>
                    <table className='bcaTestReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                <th>Correct Answer</th>
                                <th>Your Answer</th>
                                {showCorrectRate && <th>Correct %</th>}
                                <th>Time Spent</th>
                            </tr>
                            {answers.map(answer => (
                                <tr key={answer.questionNumber}>
                                    <td>{answer.questionNumber}</td>
                                    <td>{MULTIPLE_CHOICES[answer.correctChoice]}</td>
                                    <td>{yourAnswer(answer)}</td>
                                    {showCorrectRate &&
                                        <td>{answer.correctRate ? `${parseFloat(answer.correctRate).toFixed(1)}%` : '-'}</td>
                                    }
                                    <td>
                                        <p className={timeClass(answer.timeSpent)}>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/*
                Print-only: every question on its own sheet. Hidden in print by
                default and revealed only for a single-report print (see the
                `printing-single-report` rules in index.css), so a bulk print
                stays overview-only.
            */}
            <div className='bcaTestReport__questionsPrint'>
                <div className='bcaTestReport__questionsPrintSection'>
                    <div className='bcaTestReport__questionsPrintModule'>
                        <table className='bcaTestReport__questionsTable'>
                            <thead>
                                <tr>
                                    <th>Question</th>
                                    <th>Correct Answer</th>
                                    <th>Your Answer</th>
                                    <th>Time Spent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {answers.map(answer => (
                                    <tr key={`print:${answer.questionNumber}`}>
                                        <td>{answer.questionNumber}</td>
                                        <td>{MULTIPLE_CHOICES[answer.correctChoice]}</td>
                                        <td>{yourAnswer(answer)}</td>
                                        <td>
                                            <p className={timeClass(answer.timeSpent)}>
                                                {formatTime(answer.timeSpent)}
                                            </p>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BCAReport;
